import { FormikProvider, useFormik } from 'formik';
import { URL_REGEX } from 'lib/regex';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactPlayer from 'react-player';
import useUploadToS3 from 'src/hooks/useUploadToS3';
import { Button, Divider } from 'src/shared-components';
import { InputField } from 'src/shared-components/Form';
import VideoUploader from 'src/shared-components/VideoCapture/VideoUploader';
import * as Yup from 'yup';

const OnboardingFounderStepThree = forwardRef(
  (
    {
      onFormSubmitted,
      button = true,
      videoUrl,
      pitchDeckUrl,
    }: { onFormSubmitted: (values: any) => void; button?: boolean; videoUrl?: string; pitchDeckUrl?: string },
    ref,
  ) => {
    const [userUploadedVideo, setUserUploadedVideo] = useState<boolean>(false);
    const [recordNewVideo, setRecordNewVideo] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const form = useFormik({
      initialValues: {
        videoUrl: videoUrl || '',
        pitchDeckUrl: pitchDeckUrl || '',
      },
      validationSchema: Yup.object({
        videoUrl: Yup.string().matches(URL_REGEX, 'Video link should be a valid URL'),
        pitchDeckUrl: Yup.string().matches(URL_REGEX, 'Pitch deck link should be a valid URL'),
      }),
      onSubmit: values => {
        onFormSubmitted(values);
      },
    });

    useImperativeHandle(ref, () => ({
      submitForm() {
        console.log(form.errors);
        form.submitForm();
      },
    }));

    return (
      <>
        <FormikProvider value={form}>
          <div
            data-testid="onboarding-founder-step3"
            className={`grid grid-cols-6 gap-4 ${recordNewVideo ? 'hidden' : ''}`}
          >
            {userUploadedVideo ? (
              <div className="col-span-6 sm:col-span-6">
                <ReactPlayer controls={true} url={form.values.videoUrl} width="100%" height="365px" />
              </div>
            ) : (
              <>
                <div className="col-span-6 sm:col-span-6 md:col-span-3">
                  <Button
                    className="font-semibold pt-6 pb-6"
                    color="secondary"
                    size="sm"
                    roundness="lg"
                    type="button"
                    fullWidth
                    onClick={() => setRecordNewVideo(true)}
                    disabled={loading}
                  >
                    Record new video...
                  </Button>
                </div>
                <div className="col-span-6 sm:col-span-6 md:col-span-3">
                  <UploadLocalVideoFile
                    onVideoUploaded={url => {
                      setUserUploadedVideo(true);
                      form.setFieldValue('videoUrl', url);
                    }}
                    onLoading={loading => setLoading(loading)}
                  />
                </div>
                <div className="col-span-6 sm:col-span-6">
                  <Divider text="or link to a video" className="mt-4" />
                </div>
                <div className="col-span-6 sm:col-span-6">
                  <InputField
                    className="mt-3"
                    name="videoUrl"
                    autoComplete="off"
                    label=""
                    placeholder="Insert link to a video"
                    disabled={loading}
                  />
                </div>
              </>
            )}
            <div className="col-span-6 sm:col-span-6">
              <InputField
                name="pitchDeckUrl"
                autoComplete="off"
                label="pitch deck link"
                placeholder="Insert link to your pitch deck"
              />
            </div>
            <div className="col-span-6 sm:col-span-6">
              {button && (
                <Button
                  size="lg"
                  roundness="md"
                  color="black"
                  fullWidth
                  onClick={() => {
                    form.submitForm();
                  }}
                  disabled={loading}
                >
                  Next- User Profile
                </Button>
              )}
            </div>
          </div>
        </FormikProvider>
        {recordNewVideo && (
          <VideoUploader
            onVideoUploaded={url => {
              setRecordNewVideo(false);
              setUserUploadedVideo(true);
              form.setFieldValue('videoUrl', url);
            }}
          />
        )}
      </>
    );
  },
);

// const maxSize: number = 20971520; // 20MB

const UploadLocalVideoFile = ({
  onVideoUploaded,
  onLoading,
}: {
  onVideoUploaded: (url: string) => void;
  onLoading: (loading: boolean) => void;
}) => {
  const { upload, url, error, progress } = useUploadToS3();

  const onDrop = useCallback(acceptedFiles => {
    onLoading(true);
    upload(acceptedFiles[0], 'video/mp4');
  }, []);

  useEffect(() => {
    if (url) {
      console.info(url);
      onLoading(false);
      onVideoUploaded(url);
    }
  }, [url]);

  useEffect(() => {
    if (error) {
      console.log(error);
    }
  }, [error]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'video/mp4',
    multiple: false,
    // maxSize,
  });
  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <Button
        className="font-semibold pt-6 pb-6"
        color="secondary"
        size="sm"
        roundness="lg"
        type="button"
        fullWidth
        disabled={progress > 0 && progress < 100}
      >
        {progress > 0 && progress < 100 ? `Uploading... ${progress}%` : 'Upload a video...'}
      </Button>
    </div>
  );
};

export default OnboardingFounderStepThree;
